import React, { useMemo } from 'react';
import { MapPin } from 'lucide-react';
import { ItemLocationBalanceDto } from '../../../../../types/itemLocationBalance';
import { AdvancedItemConfigDto } from '../../../../../types/advancedItemConfig';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { hasExpiryDate, isTrackedBatch, isTrackedSerial } from '../../../../../utils/itemType';
import { formatDate, formatQty } from '../format';
import { SectionCard } from './SectionCard';

interface ItemLocationsSectionProps {
  bins: ItemLocationBalanceDto[];
  itemType: number | null | undefined;
  advancedConfig: AdvancedItemConfigDto | null;
  /** Items.StdBinCode — «Fast plukklokasjon» (WMS). */
  stdBinCode?: string | null;
  /** Items.StdLocationName — «Standard lokasjon» (fra ERP). */
  stdLocationName?: string | null;
}

/**
 * «Hvor er den?» — lokasjoner med saldo. Dedikert lokasjon, fast plukklokasjon og standard
 * lokasjon sorteres først og merkes, deretter størst beholdning. Lokasjoner med 0 vises ikke.
 * Batch/utløp vises kun for lagerstyrt batch (4/5), serienr kun for 6.
 */
export const ItemLocationsSection: React.FC<ItemLocationsSectionProps> = ({ bins, itemType, advancedConfig, stdBinCode, stdLocationName }) => {
  const { t } = useTranslation();
  const showBatch = isTrackedBatch(itemType);
  const showExpiry = hasExpiryDate(itemType);
  const showSerial = isTrackedSerial(itemType);

  const fixedPickBin = (advancedConfig?.stdBinCode || stdBinCode || '').toLowerCase();
  const stdLocation = (stdLocationName || '').toLowerCase();
  const dedicatedBin = (advancedConfig?.dedicatedBinCode || '').toLowerCase();

  const rows = useMemo(() => {
    const rank = (b: ItemLocationBalanceDto) => {
      const code = b.binCode.toLowerCase();
      if (dedicatedBin && code === dedicatedBin) return 0;
      if (fixedPickBin && code === fixedPickBin) return 1;
      if (stdLocation && code === stdLocation) return 2;
      return 3;
    };
    return bins
      .filter(b => (b.quantity ?? 0) !== 0)
      .sort((a, b) => rank(a) - rank(b) || (b.quantity ?? 0) - (a.quantity ?? 0));
  }, [bins, fixedPickBin, stdLocation, dedicatedBin]);

  const badge = (text: string, classes: string) =>
    <span className={`ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded ${classes}`}>{text}</span>;

  const marker = (binCode: string) => {
    const code = binCode.toLowerCase();
    if (dedicatedBin && code === dedicatedBin) {
      return badge(t('warehouse.modules.itemCard.locations.dedicated', {}, 'Dedikert'), 'bg-purple-100 text-purple-700');
    }
    if (fixedPickBin && code === fixedPickBin) {
      return badge(t('warehouse.modules.itemCard.locations.fixedPick', {}, 'Fast plukk'), 'bg-blue-100 text-blue-700');
    }
    if (stdLocation && code === stdLocation) {
      return badge(t('warehouse.modules.itemCard.locations.standard', {}, 'Standard'), 'bg-gray-100 text-gray-700');
    }
    return null;
  };

  return (
    <SectionCard
      icon={<MapPin className="h-4 w-4" />}
      title={t('warehouse.modules.itemCard.locations.title', {}, 'Hvor er den?')}
      count={rows.length}
    >
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">{t('warehouse.modules.itemCard.locations.empty', {}, 'Ingen beholdning på lokasjon')}</p>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:-mx-5">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('warehouse.modules.itemCard.locations.bin', {}, 'Lokasjon')}</th>
                <th className="hidden sm:table-cell px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('warehouse.modules.itemCard.locations.zone', {}, 'Sone')}</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('warehouse.modules.itemCard.locations.quantity', {}, 'Antall')}</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('warehouse.modules.itemCard.locations.available', {}, 'Tilgj.')}</th>
                {showBatch && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('warehouse.modules.itemCard.locations.batch', {}, 'Batch')}</th>}
                {showExpiry && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('warehouse.modules.itemCard.locations.expiry', {}, 'Utløper')}</th>}
                {showSerial && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('warehouse.modules.itemCard.locations.serial', {}, 'Serienr.')}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((bin, i) => (
                <tr key={`${bin.warehouseID}-${bin.binCode}-${bin.serialNo || bin.batchNo || i}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 font-mono text-sm whitespace-nowrap">{bin.binCode}{marker(bin.binCode)}</td>
                  <td className="hidden sm:table-cell px-3 py-2.5 text-xs text-gray-600">{bin.zoneDescription || bin.stcZoneID || '-'}</td>
                  <td className="px-3 py-2.5 text-right font-semibold">{formatQty(bin.quantity)}</td>
                  <td className="px-3 py-2.5 text-right text-green-600">{formatQty(bin.quantityAvailable)}</td>
                  {showBatch && <td className="px-3 py-2.5 text-xs font-mono">{bin.batchNo || '-'}</td>}
                  {showExpiry && <td className="px-3 py-2.5 text-xs">{formatDate(bin.expiryDate)}</td>}
                  {showSerial && <td className="px-3 py-2.5 text-xs font-mono">{bin.serialNo || '-'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
};
